import { SITE_URL_MISSING } from "../../_redux/conectiaValidation/conectiaValidationCrud";

/** The three statuses Conectia accepts back, in the order the screen offers them. */
export const VALIDATION_STATUSES = ["accepted", "rejected", "pending"];

/**
 * A status as shown: "accepted" becomes "Accepted".
 * @param {string|null|undefined} status - Lower-case status from the API.
 * @returns {string} The label, or "-" when there is none.
 */
export function statusLabel(status) {
  if (!status) return "-";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * Metronic label colour for a status.
 * @param {string} status - accepted, rejected or pending.
 * @returns {string} The `label-light-*` suffix.
 */
export function statusColor(status) {
  if (status === "accepted") return "success";
  if (status === "rejected") return "danger";
  return "warning";
}

/**
 * The starting decision for every line: the server's suggestion, no reason.
 * @param {Array<{line: number, suggested_status: string}>} rows - Checked lines.
 * @returns {Record<number, {status: string, reason: string}>} Decisions keyed by file line.
 */
export function initialDecisions(rows) {
  const decisions = {};
  (rows || []).forEach((row) => {
    decisions[row.line] = { status: row.suggested_status, reason: "" };
  });
  return decisions;
}

/**
 * Whether a line's decision differs from the suggestion.
 * @param {{suggested_status: string}} row - Checked line.
 * @param {{status: string}|undefined} decision - The decision for that line.
 * @returns {boolean} True when the status was changed by hand.
 */
export function isOverride(row, decision) {
  return Boolean(decision) && decision.status !== row.suggested_status;
}

/**
 * Lines whose status was changed but have no reason yet.
 * @param {Array<object>} rows - Checked lines.
 * @param {Record<number, {status: string, reason: string}>} decisions - Decisions keyed by line.
 * @returns {number[]} File line numbers missing a reason.
 */
export function linesMissingReason(rows, decisions) {
  return (rows || [])
    .filter((row) => isOverride(row, decisions[row.line]) && !decisions[row.line].reason.trim())
    .map((row) => row.line);
}

/**
 * The changed lines, as the download endpoint takes them.
 * @param {Array<object>} rows - Checked lines.
 * @param {Record<number, {status: string, reason: string}>} decisions - Decisions keyed by line.
 * @returns {Array<{line: number, status: string, reason: string}>} One entry per changed line.
 */
export function overridesForDownload(rows, decisions) {
  return (rows || [])
    .filter((row) => isOverride(row, decisions[row.line]))
    .map((row) => ({
      line: row.line,
      status: decisions[row.line].status,
      reason: decisions[row.line].reason.trim(),
    }));
}

/**
 * What saving a line would store: its status, and its reason only when the
 * status was changed (a line set back to its suggestion goes with an empty
 * reason, not whatever was typed before).
 * @param {{line: number, suggested_status: string}} row - Checked line.
 * @param {Record<number, {status: string, reason: string}>} decisions - Decisions keyed by line.
 * @returns {{status: string, reason: string}} The line's decision as saved.
 */
export function savedDecision(row, decisions) {
  const decision = decisions[row.line] || { status: row.suggested_status, reason: "" };
  return {
    status: decision.status,
    reason: isOverride(row, decision) ? decision.reason.trim() : "",
  };
}

/**
 * Bookings on more than one line whose lines carry different decisions
 * (status or reason). The save stores one decision per booking while the reply
 * sends every line's own, so the two would disagree: the page lists these and
 * refuses to save or download until they match.
 * @param {Array<object>} rows - Checked lines.
 * @param {Record<number, {status: string, reason: string}>} decisions - Decisions keyed by line.
 * @returns {Array<{orderId: number, bookingReference: string, lines: Array<{line: number, status: string, reason: string}>}>}
 *   One entry per such booking, in file order, with each of its lines.
 */
export function conflictingBookings(rows, decisions) {
  const byOrder = new Map();
  (rows || []).forEach((row) => {
    if (!row.order) return;
    if (!byOrder.has(row.order.id)) {
      byOrder.set(row.order.id, {
        orderId: row.order.id,
        bookingReference: row.order.booking_reference,
        lines: [],
      });
    }
    byOrder.get(row.order.id).lines.push({ line: row.line, ...savedDecision(row, decisions) });
  });
  return Array.from(byOrder.values()).filter(({ lines }) =>
    lines.some((entry) => entry.status !== lines[0].status || entry.reason !== lines[0].reason),
  );
}

/**
 * What a save stores, line by line, for the matched lines: the snapshot the
 * page keeps after a successful save.
 * @param {Array<object>} rows - Checked lines.
 * @param {Record<number, {status: string, reason: string}>} decisions - Decisions keyed by line.
 * @returns {Record<number, {status: string, reason: string}>} savedDecision() per matched line.
 */
export function savedSnapshot(rows, decisions) {
  const snapshot = {};
  (rows || []).forEach((row) => {
    if (row.order) snapshot[row.line] = savedDecision(row, decisions);
  });
  return snapshot;
}

/**
 * Whether the decisions on screen differ from the last successful save, so a
 * reply downloaded now would tell Conectia something the bookings don't hold.
 * Only matched lines count (nothing else is saved); with none, there is
 * nothing to save and nothing can differ.
 * @param {Array<object>} rows - Checked lines.
 * @param {Record<number, {status: string, reason: string}>} decisions - Decisions keyed by line.
 * @param {Record<number, {status: string, reason: string}>|null} saved - savedSnapshot() from the last save, or null.
 * @returns {boolean} True when a save is needed before downloading.
 */
export function hasUnsavedDecisions(rows, decisions, saved) {
  const matched = (rows || []).filter((row) => row.order);
  if (!matched.length) return false;
  if (!saved) return true;
  return matched.some((row) => {
    const now = savedDecision(row, decisions);
    const then = saved[row.line];
    return !then || then.status !== now.status || then.reason !== now.reason;
  });
}

/**
 * The decisions for matched bookings, as the save endpoint takes them. A
 * booking on several lines is sent once (the last line wins, as on the server);
 * the page only saves when those lines agree (see conflictingBookings).
 * @param {Array<object>} rows - Checked lines.
 * @param {Record<number, {status: string, reason: string}>} decisions - Decisions keyed by line.
 * @returns {Array<{order_id: number, status: string, reason: string}>} One entry per booking.
 */
export function linesForSave(rows, decisions) {
  const byOrder = new Map();
  (rows || []).forEach((row) => {
    if (!row.order) return;
    byOrder.set(row.order.id, { order_id: row.order.id, ...savedDecision(row, decisions) });
  });
  return Array.from(byOrder.values());
}

/**
 * The month before today as `YYYY-MM`: the activity a report sent this month covers.
 * @param {Date} [today=new Date()] - Today.
 * @returns {string} The month.
 */
export function previousMonth(today = new Date()) {
  const date = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

/** Shown when the admin site was built without its API address. */
export const SITE_URL_MISSING_TEXT =
  "This admin site has no API address (REACT_APP_SITE_URL is not set), so nothing was sent. Ask a developer to set it.";

/**
 * The message in an error whose body came back as a Blob (a failed file
 * download), or the usual JSON message otherwise. The crud's missing
 * `REACT_APP_SITE_URL` error (code SITE_URL_MISSING) gets its own fixed text,
 * so a set-up fault is not dressed up as a failed check; no other error's own
 * text is shown.
 * @param {unknown} error - Axios error, or the crud's set-up error.
 * @param {string} fallback - Message when the server gave none.
 * @returns {Promise<string>} The message to show.
 */
export async function errorMessage(error, fallback) {
  if (error?.code === SITE_URL_MISSING) {
    return SITE_URL_MISSING_TEXT;
  }
  const data = error?.response?.data;
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      return JSON.parse(await data.text())?.message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.message || fallback;
}
